import Highcharts from 'highcharts';
import HighchartsMore from 'highcharts/highcharts-more';
import { useMemo } from 'react';
import StoneXChart from '../../../StoneX/StoneXChart';
import { readCssColorVariable, readDecileColor } from '../DecileMatrixStyles';
import { MatrixForwardCurveSeries, MatrixGraphPoint, MatrixTermResult, MatrixYearPriceSeries } from './MatrixTypes';

HighchartsMore(Highcharts);

interface MatrixChartProps {
  graphPoints: MatrixGraphPoint[];
  yearPriceSeries: MatrixYearPriceSeries[];
  forwardCurveSeries: MatrixForwardCurveSeries[];
  selectedPercentiles: number[];
  minPercentile: number | null;
  maxPercentile: number | null;
  historyMode: boolean;
  currencySymbol: string;
}

// Matches --decile-0..90 in index.css exactly — the matrix uses 5%-step percentiles, so each
// decade's color also covers its +5 offset (e.g. 45% uses the 40% color), and anything at or
// above 90% (including 100%) uses the 90% color.
const DECILE_BAND_THRESHOLDS = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90];

function getMatrixBandColor(lowDecile: number): string {
  const threshold = [...DECILE_BAND_THRESHOLDS].reverse().find((t) => t <= lowDecile) ?? 0;
  return readDecileColor(threshold);
}

const MATRIX_OPACITY: Record<number, string> = {
  0: '44',
  5: '44',
  10: '55',
  15: '55',
  20: '77',
  25: '77',
  30: '99',
  35: '99',
  40: 'FF',
  45: 'FF',
  50: 'FF',
  55: 'FF',
  60: '99',
  65: '99',
  70: '77',
  75: '77',
  80: '55',
  85: '55',
  90: '44',
  95: '44',
  100: '44',
};

const YEAR_LINE_COLOR_VARIABLES = [
  '--matrix-year-line-1', '--matrix-year-line-2', '--matrix-year-line-3', '--matrix-year-line-4',
  '--matrix-year-line-5', '--matrix-year-line-6', '--matrix-year-line-7', '--matrix-year-line-8',
];

const TERM_ORDER: string[] = ['Annual', 'Summer', 'Winter', 'Q1', 'Q2', 'Q3', 'Q4', 'January-February', 'March-April', 'July-August', 'October-December'];

function getTermSortIndex(term: string): number {
  const index = TERM_ORDER.indexOf(term);
  return index >= 0 ? index : TERM_ORDER.length;
}

export default function MatrixChart({ graphPoints, yearPriceSeries, forwardCurveSeries, selectedPercentiles, minPercentile, maxPercentile, historyMode, currencySymbol }: MatrixChartProps) {
  // Get list of terms from the data, sorted by TERM_ORDER
  const terms = useMemo(() => {
    if (graphPoints.length === 0) return [];
    const lastPoint = graphPoints[graphPoints.length - 1];
    return lastPoint.terms
      .map(t => t.season)
      .sort((a, b) => getTermSortIndex(a) - getTermSortIndex(b));
  }, [graphPoints]);

  const chartOptionsPerTerm = useMemo(() => {
    if (graphPoints.length === 0 || terms.length === 0) return [];

    return terms.map((termSeason) => {
      // Resolve the actual index in graphPoints.terms (which may differ from sorted order)
      const termIndex = graphPoints[graphPoints.length - 1].terms.findIndex(t => t.season === termSeason);

      // Filter yearPriceSeries for this term - match by term name, or include all if only one term selected
      // Backend uses empty string for Annual in yearPriceSeries, so match both
      const termYearPrices = terms.length === 1
        ? yearPriceSeries
        : yearPriceSeries.filter(yps => yps.term === termSeason || (termSeason === 'Annual' && yps.term === ''));

      const termForwardCurve = terms.length === 1
        ? forwardCurveSeries
        : forwardCurveSeries.filter(fc => fc.term === termSeason);

      if (historyMode && graphPoints.length > 1) {
        return buildTermChart(graphPoints, termIndex, termSeason, termYearPrices, termForwardCurve, selectedPercentiles, minPercentile, maxPercentile, true, currencySymbol);
      } else {
        return buildTermChart(graphPoints, termIndex, termSeason, termYearPrices, termForwardCurve, selectedPercentiles, minPercentile, maxPercentile, false, currencySymbol);
      }
    });
  }, [graphPoints, yearPriceSeries, forwardCurveSeries, selectedPercentiles, minPercentile, maxPercentile, historyMode, terms, currencySymbol]);

  if (chartOptionsPerTerm.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {chartOptionsPerTerm.map((options, index) => (
        <div key={terms[index]}>
          <h4 style={{ margin: '0 0 4px 8px', color: 'var(--font-color)' }}>{terms[index]}</h4>
          <StoneXChart options={options} height={400} />
        </div>
      ))}
    </div>
  );
}

function buildTermChart(
  graphPoints: MatrixGraphPoint[],
  termIndex: number,
  termSeason: string,
  termYearPrices: MatrixYearPriceSeries[],
  termForwardCurve: MatrixForwardCurveSeries[],
  selectedPercentiles: number[],
  minPercentile: number | null,
  maxPercentile: number | null,
  stepped: boolean,
  currencySymbol: string,
): Highcharts.Options {
  const sortedDeciles = [...selectedPercentiles].sort((a, b) => a - b);
  const series: Highcharts.SeriesOptionsType[] = [];

  // Determine the time range from yearPriceSeries (for flat bands in non-history mode)
  let timeMin = Infinity;
  let timeMax = -Infinity;
  for (const yps of termYearPrices) {
    for (const p of yps.dailyPrices) {
      const t = new Date(p.date).getTime();
      if (t < timeMin) { timeMin = t; }
      if (t > timeMax) { timeMax = t; }
    }
  }
  // Extend range to include forward curve dates
  for (const curveSeries of termForwardCurve) {
    for (const p of curveSeries.prices) {
      const t = Date.UTC(p.contractYear, p.contractMonth - 1, 1);
      if (t < timeMin) { timeMin = t; }
      if (t > timeMax) { timeMax = t; }
    }
  }
  // Fallback: use a 1-year range ending at the last graph point date
  if (timeMin === Infinity || timeMin === timeMax) {
    const lastDate = new Date(graphPoints[graphPoints.length - 1].calculationDate);
    timeMax = lastDate.getTime();
    timeMin = new Date(lastDate.getFullYear() - 1, lastDate.getMonth(), lastDate.getDate()).getTime();
  }

  // Build decile bands

  for (let i = 0; i < sortedDeciles.length - 1; i++) {
    const lowDecile = sortedDeciles[i];
    const highDecile = sortedDeciles[i + 1];
    const lowRank = lowDecile / 100;
    const highRank = highDecile / 100;

    const baseColor = getMatrixBandColor(lowDecile);
    const opacity = MATRIX_OPACITY[lowDecile] ?? '99';

    if (stepped) {
      // History mode: bands step at each graph point's calculation date
      const data = graphPoints.map(point => {
        const termResult = point.terms[termIndex];
        if (!termResult) { return [new Date(point.calculationDate).getTime(), 0, 0]; }
        const lowValue = termResult.weightedDeciles.find(d => Math.abs(d.rank - lowRank) < 0.001)?.value ?? 0;
        const highValue = termResult.weightedDeciles.find(d => Math.abs(d.rank - highRank) < 0.001)?.value ?? 0;
        return [new Date(point.calculationDate).getTime(), lowValue, highValue];
      });

      series.push({
        name: `${lowDecile}% - ${highDecile}%`,
        type: 'arearange',
        data: data,
        color: `${baseColor}${opacity}`,
        fillColor: `${baseColor}${opacity}`,
        lineColor: `${baseColor}${opacity}`,
        lineWidth: 1,
        marker: { enabled: false },
        showInLegend: false,
        step: 'left',
        label: { enabled: false },
        enableMouseTracking: true,
        zIndex: 1,
      } as Highcharts.SeriesArearangeOptions);
    } else {
      // Non-history: flat arearange bands spanning the full x-axis range
      const termResult = graphPoints[graphPoints.length - 1].terms[termIndex];
      const lowValue = termResult?.weightedDeciles.find(d => Math.abs(d.rank - lowRank) < 0.001)?.value ?? 0;
      const highValue = termResult?.weightedDeciles.find(d => Math.abs(d.rank - highRank) < 0.001)?.value ?? 0;

      series.push({
        name: `${lowDecile}% - ${highDecile}%`,
        type: 'arearange',
        data: [[timeMin, lowValue, highValue], [timeMax, lowValue, highValue]],
        color: `${baseColor}${opacity}`,
        fillColor: `${baseColor}${opacity}`,
        lineColor: `${baseColor}${opacity}`,
        lineWidth: 1,
        marker: { enabled: false },
        showInLegend: false,
        label: { enabled: false },
        enableMouseTracking: true,
        zIndex: 1,
      } as Highcharts.SeriesArearangeOptions);
    }
  }

  // Add daily year price lines
  termYearPrices.forEach((yearSeries, index) => {
    const data = yearSeries.dailyPrices
      .filter(p => p.price != null)
      .map(p => [new Date(p.date).getTime(), p.price as number]);

    const label = `${yearSeries.year}`;

    series.push({
      name: label,
      type: 'line',
      data: data,
      color: readCssColorVariable(YEAR_LINE_COLOR_VARIABLES[index % YEAR_LINE_COLOR_VARIABLES.length]),
      lineWidth: 2,
      marker: { enabled: false },
      zIndex: 5,
    } as Highcharts.SeriesLineOptions);
  });

  // Add forward curve (last settlement) line
  termForwardCurve.forEach((curveSeries) => {
    const data = curveSeries.prices.map(p => {
      const contractDate = Date.UTC(p.contractYear, p.contractMonth - 1, 1);
      return [contractDate, p.price as number];
    });

    if (data.length > 0) {
      series.push({
        name: `Last Settlement (${new Date(curveSeries.quoteDate).toLocaleDateString()})`,
        type: 'line',
        data: data,
        color: readCssColorVariable('--matrix-forward-curve-line'),
        lineWidth: 2.5,
        marker: { enabled: true, radius: 3 },
        zIndex: 6,
        dashStyle: 'ShortDash',
      } as Highcharts.SeriesLineOptions);
    }
  });

  // Compute y-axis bounds when min/max percentile is set
  let yAxisMax: number | undefined = undefined;
  let yAxisMin: number | undefined = undefined;
  if (minPercentile != null) {
    const minRank = minPercentile / 100;
    let minPercentilePrice = Infinity;
    for (const point of graphPoints) {
      const termResult = point.terms[termIndex];
      if (!termResult) { continue; }
      const value = termResult.weightedDeciles.find(d => Math.abs(d.rank - minRank) < 0.001)?.value ?? Infinity;
      if (value < minPercentilePrice) { minPercentilePrice = value; }
    }
    if (minPercentilePrice !== Infinity) {
      yAxisMin = minPercentilePrice;
    }
  }
  if (maxPercentile != null) {
    const maxRank = maxPercentile / 100;
    let maxPercentilePrice = 0;
    for (const point of graphPoints) {
      const termResult = point.terms[termIndex];
      if (!termResult) { continue; }
      const value = termResult.weightedDeciles.find(d => Math.abs(d.rank - maxRank) < 0.001)?.value ?? 0;
      if (value > maxPercentilePrice) { maxPercentilePrice = value; }
    }
    if (maxPercentilePrice > 0) {
      yAxisMax = maxPercentilePrice;
    }
  }

  // Add a small pad when clipping is active so band edges are still visible.
  if (yAxisMin != null && yAxisMax != null) {
    const range = yAxisMax - yAxisMin;
    if (range <= 0) {
      const center = yAxisMin;
      yAxisMin = center - Math.abs(center * 0.01);
      yAxisMax = center + Math.abs(center * 0.01);
    } else {
      const pad = range * 0.02;
      yAxisMin -= pad;
      yAxisMax += pad;
    }
  } else if (yAxisMin != null) {
    yAxisMin *= 0.99;
  } else if (yAxisMax != null) {
    yAxisMax *= 1.01;
  }

  // When bands-only (no year/forward curve lines), ensure chart renders with correct y-axis range
  const bandSeries = series.filter(s => s.type === 'arearange');
  const lineSeries = series.filter(s => s.type !== 'arearange');
  if (lineSeries.length === 0 && bandSeries.length > 0 && minPercentile == null && maxPercentile == null) {
    const allData = bandSeries.flatMap(s => (s as any).data ?? []);
    const bandMin = Math.min(...allData.map((d: any) => d[1]));
    const bandMax = Math.max(...allData.map((d: any) => d[2]));
    yAxisMin = bandMin * 0.95;
    yAxisMax = bandMax * 1.05;
  }

  return {
    chart: { type: 'line' },
    title: { text: undefined },
    xAxis: {
      type: 'datetime',
      labels: { format: '{value:%b %Y}' },
      crosshair: true,
    },
    yAxis: {
      title: { text: 'Price' },
      labels: { format: `${currencySymbol}{value:.2f}` },
      min: yAxisMin,
      max: yAxisMax,
      gridZIndex: 0,
    },
    plotOptions: {
      series: {
        states: {
          inactive: {
            opacity: 1,
          },
        },
      },
    },
    tooltip: {
      shared: true,
      formatter: function(this: Highcharts.TooltipFormatterContextObject): string {
        const dateLabel = Highcharts.dateFormat('%b %e, %Y', this.x as number);
        let html = `<b>${dateLabel}</b><br/>`;
        for (const point of this.points ?? []) {
          if (point.series.type === 'arearange') {
            const low = (point.point as any).low as number;
            const high = (point.point as any).high as number;
            html += `<span style="color:${point.color as string}">●</span> ${point.series.name}: ${currencySymbol}${low.toFixed(4)} – ${currencySymbol}${high.toFixed(4)}<br/>`;
          } else {
            const y = point.y as number;
            html += `<span style="color:${point.color as string}">●</span> <b>${point.series.name}</b>: ${currencySymbol}${y.toFixed(4)}<br/>`;
          }
        }
        return html;
      },
    },
    legend: { enabled: true },
    series,
    credits: { enabled: false },
  };
}
